import React, { useMemo } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PortraitIcon from '@mui/icons-material/Portrait';
import { Box, IconButton, Slider, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Cropper from 'react-easy-crop';
import { PROFILE_PICTURE, PROFILE_PICTURE_SPECS } from 'common/constants/user';
import { DropZone } from 'components/';
import { validImageExtensions } from 'components/Documents/imageTypes';
import { useImageUpload } from 'hooks/useImageUpload/useImageUpload';
import { ImageContainerProps } from './ImageContainer.props';

const ImageContainer = ({
  imageSrc: externalImageSrc,
  setImageSrc,
  setSelectedFile,
  setCroppedAreaPixels,
  title = PROFILE_PICTURE,
  cropShape = 'round',
  aspect,
}: ImageContainerProps) => {

  const {
    imageSrc,
    crop,
    zoom,
    setCrop,
    setZoom,
    onCropComplete,
    handleFileProcess,
    reset
  } = useImageUpload({
    onCropCompleteAction: setCroppedAreaPixels,
    initialImage: externalImageSrc
  });

  const onDrop = async ({ file }: { file: File | null }) => {
    if (!file) {
      return;
    }
    const dataUrl = await handleFileProcess(file);
    setSelectedFile(file);
    setImageSrc(dataUrl);
  };

  const handleReset = () => {
    reset();
    setSelectedFile(null);
    setImageSrc(null);
  };

  const titleArray = useMemo(() => [
    [`Drag & Drop ${title}`, {}],
    ['or', {}],
    ['Select a file', { textDecoration: 'underline' }],
  ], [title]);

  const calculatedAspect = aspect ? aspect.horizontal / aspect.vertical : 1;


  return (
    <Stack spacing={2} sx={{ width: '100%', alignItems: 'flex-start' }}>
      <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>

      <Box sx={{ width: '100%', minHeight: 320, position: 'relative' }}>
        {imageSrc ? (
          <Box sx={{ maxWidth: 500 }}>
            <Box sx={{ 
              position: 'relative', 
              height: 280, 
              width: '100%', 
              bgcolor: 'grey.100', 
              borderRadius: 2,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={calculatedAspect}
                cropShape={cropShape}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
                showGrid={false}
              />
            </Box>

            <Grid container spacing={2} alignItems="center" sx={{ mt: 1.5, px: 1 }}>
              <Grid>
                <PortraitIcon color={zoom > 1 ? 'primary' : 'disabled'} fontSize="small" />
              </Grid>
              <Grid size="grow">
                <Slider
                  value={zoom}
                  min={1}
                  max={3}
                  step={0.1}
                  onChange={(_, val) => setZoom(val as number)}
                  aria-label="Zoom"
                />
              </Grid>
              <Grid>
                <PortraitIcon color={zoom < 3 ? 'primary' : 'disabled'} fontSize="medium" />
              </Grid>
              <Grid>
                <IconButton onClick={handleReset} size="small" color="error" title="Delete image">
                  <DeleteOutlineIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ) : (
          <DropZone
            setFileSrc={onDrop}
            subTitle={PROFILE_PICTURE_SPECS}
            acceptFileTypes="image/*"
            validFileTypes={validImageExtensions}
            maxSizeAllowedInMb={15}
            titleArray={titleArray}
            allowMultipleFiles={false}
          />
        )}
      </Box>
    </Stack>
  );
};

export default ImageContainer;